// client/src/components/secure/training/SlidePlayer.js — the slide-video player.
// Full-width slides (heading, bullets, visual) with narration the browser reads aloud through
// the built-in Web Speech API (speechSynthesis, an en-US voice, adjustable rate). Without
// speech (or with the voice off) it advances on a timer and shows the narration as captions.
// Play/Pause, previous/next, a progress bar, captions, a transcript, keyboard controls and
// swipe on phones. onComplete fires once: last slide reached, or ≥90% of slides viewed.
// No video hosting, nothing paid: the "video" is the slides plus the browser's own voice.
// A slide may carry a screenshot (image_src, a signed /training-media/ link added by the server
// from the slide's `image`): it is shown large, with the heading above and the bullets beside it
// (below it on a phone); tap it to open it full size. The next slide's picture is preloaded.
// A slide may also carry recorded narration (audio_src, from the slide's `audio` MP3): it is played
// instead of the browser voice, at the chosen speed, and the slide advances when it ends. A slide
// without audio (or whose audio fails) uses the browser voice, else the timer; captions as usual.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Markdown } from './shared';
import {
    clampIndex, estimateSeconds, hasRecordedNarration, isPlayerComplete, markSeen, narrationMode, nextImageSrc, pickVoice,
    progressPercent, safeRate, slideAudioSrc, slideImageSrc, slideKeyAction, splitSentences, swipeDirection,
} from './slideLogic';

const speechAvailable = () => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance === 'function';
const RATES = [0.75, 1, 1.25, 1.5];

const SlidePlayer = ({ content, notesMd, onComplete, completed }) => {
    const slides = useMemo(() => ((content && Array.isArray(content.slides)) ? content.slides : []), [content]);
    const n = slides.length;
    const canSpeak = useMemo(speechAvailable, []);
    const recorded = useMemo(() => hasRecordedNarration(slides), [slides]);
    const voiceAvailable = canSpeak || recorded;
    const [idx, setIdx] = useState(0);
    const [sIdx, setSIdx] = useState(0);              // sentence of the narration being spoken
    const [seen, setSeen] = useState([0]);
    const [playing, setPlaying] = useState(false);
    const [voiceOn, setVoiceOn] = useState(true);
    const [audioFailed, setAudioFailed] = useState({});   // slide index → its audio would not play
    const [rate, setRate] = useState(1);
    const [captions, setCaptions] = useState(true);
    const [transcript, setTranscript] = useState(false);
    const [voices, setVoices] = useState([]);
    const doneRef = useRef(!!completed);
    const touch = useRef(null);
    const audioRef = useRef(null);                      // one <audio> element for the whole lesson
    const boxRef = useRef(null);

    const slide = slides[clampIndex(idx, n)] || {};
    const sentences = useMemo(() => splitSentences(slide.narration), [slide.narration]);
    const img = slideImageSrc(slide);
    const audioSrc = slideAudioSrc(slide);
    const mode = narrationMode(slide, voiceOn && voiceAvailable, canSpeak, !!audioFailed[clampIndex(idx, n)]);
    const nextImg = nextImageSrc(slides, clampIndex(idx, n));

    // Preload the next slide's screenshot so it appears without a gap.
    useEffect(() => {
        if (!nextImg || typeof window === 'undefined' || typeof window.Image !== 'function') return;
        const im = new window.Image();
        im.src = nextImg;
    }, [nextImg]);

    // Voices arrive asynchronously in most browsers.
    useEffect(() => {
        if (!canSpeak) return undefined;
        const load = () => setVoices(window.speechSynthesis.getVoices() || []);
        load();
        window.speechSynthesis.addEventListener && window.speechSynthesis.addEventListener('voiceschanged', load);
        return () => {
            window.speechSynthesis.removeEventListener && window.speechSynthesis.removeEventListener('voiceschanged', load);
            window.speechSynthesis.cancel();
        };
    }, [canSpeak]);
    const voice = useMemo(() => pickVoice(voices, 'en-US'), [voices]);

    const go = useCallback((i) => {
        const j = clampIndex(i, n);
        setIdx(j);
        setSIdx(0);
        setSeen((s) => markSeen(s, j));
    }, [n]);

    // Completion: last slide reached or ≥90% viewed.
    useEffect(() => {
        if (!doneRef.current && isPlayerComplete(seen, n)) {
            doneRef.current = true;
            onComplete && onComplete();
        }
    }, [seen, n, onComplete]);

    // Recorded narration: play the slide's MP3 at the chosen speed; next slide when it ends.
    // Pausing keeps the place; changing slide starts that slide's file from the beginning.
    useEffect(() => {
        if (mode !== 'audio') { if (audioRef.current) audioRef.current.pause(); return undefined; }
        if (typeof window === 'undefined' || typeof window.Audio !== 'function') return undefined;
        if (!audioRef.current) audioRef.current = new window.Audio();
        const a = audioRef.current;
        const src = audioSrc;
        const here = clampIndex(idx, n);
        const key = `${here}|${src}`;
        if (a.dataset.key !== key) { a.src = src; a.dataset.key = key; }
        a.playbackRate = safeRate(rate);
        a.onended = () => { a.dataset.key = ''; if (here + 1 < n) go(here + 1); else setPlaying(false); };
        a.onerror = () => setAudioFailed((f) => ({ ...f, [here]: true }));
        if (playing) {
            const p = a.play();
            // Autoplay refused: wait for Play. Anything else (file missing): the browser voice takes over.
            if (p && p.catch) p.catch((e) => { if (e && e.name === 'NotAllowedError') setPlaying(false); else if (!e || e.name !== 'AbortError') setAudioFailed((f) => ({ ...f, [here]: true })); });
        } else a.pause();
        return () => { a.onended = null; a.onerror = null; a.pause(); };
    }, [mode, playing, idx, n, audioSrc, rate, go]);
    useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; } }, []);

    // Playback: speak (or time) one sentence at a time, then the next, then the next slide.
    useEffect(() => {
        if (!playing || !n || mode === 'audio') return undefined;
        let live = true;
        let timer = null;
        const advance = () => {
            if (!live) return;
            if (sIdx + 1 < sentences.length) setSIdx(sIdx + 1);
            else if (idx + 1 < n) go(idx + 1);
            else setPlaying(false);
        };
        const text = sentences[sIdx] || '';
        if (mode === 'speech' && text) {
            const u = new window.SpeechSynthesisUtterance(text);
            if (voice) u.voice = voice;
            u.lang = (voice && voice.lang) || 'en-US';
            u.rate = rate;
            u.onend = advance;
            u.onerror = () => { if (live) timer = setTimeout(advance, estimateSeconds(text, rate) * 1000); };
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(u);
        } else {
            timer = setTimeout(advance, (text ? estimateSeconds(text, rate) : 3) * 1000);
        }
        return () => {
            live = false;
            if (timer) clearTimeout(timer);
            if (canSpeak) window.speechSynthesis.cancel();
        };
    }, [playing, idx, sIdx, sentences, mode, canSpeak, voice, rate, n, go]);

    const toggle = useCallback(() => {
        if (!playing && idx === n - 1 && sIdx >= sentences.length - 1 && mode !== 'audio') go(0);
        setPlaying((p) => !p);
    }, [playing, idx, n, sIdx, sentences.length, go, mode]);

    const act = useCallback((a) => {
        if (a === 'next') go(idx + 1);
        else if (a === 'prev') go(idx - 1);
        else if (a === 'first') go(0);
        else if (a === 'last') go(n - 1);
        else if (a === 'toggle') toggle();
        else if (a === 'captions') setCaptions((c) => !c);
        else if (a === 'transcript') setTranscript((t) => !t);
    }, [go, idx, n, toggle]);

    // Keyboard: while the player is on screen, outside form fields.
    useEffect(() => {
        const onKey = (e) => {
            if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
            const tag = (e.target && e.target.tagName) || '';
            if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (e.target && e.target.isContentEditable)) return;
            if (tag === 'BUTTON' && (e.key === ' ' || e.key === 'Enter')) return;
            if (boxRef.current && !document.body.contains(boxRef.current)) return;
            const a = slideKeyAction(e.key);
            if (a) { e.preventDefault(); act(a); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [act]);

    const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
    const onTouchEnd = (e) => {
        if (!touch.current) return;
        const t = e.changedTouches[0];
        const dir = swipeDirection(t.clientX - touch.current.x, t.clientY - touch.current.y);
        touch.current = null;
        if (dir) act(dir);
    };

    if (!n) return <p className="text-sm text-slate-600">This lesson has no slides yet.</p>;
    const pct = progressPercent(idx, n);
    const caption = playing && mode !== 'audio' ? (sentences[sIdx] || '') : (slide.narration || '');
    return (
        <div ref={boxRef} className="space-y-2" aria-roledescription="slide video">
            <div className="relative w-full rounded-lg overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-slate-900 text-white select-none"
                style={{ minHeight: '18rem' }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {img ? (
                    <div className="p-3 sm:p-6 pb-24 sm:pb-24" aria-live="polite">
                        <p className="text-xs uppercase tracking-wider text-indigo-200 mb-1">Slide {idx + 1} of {n}</p>
                        <h3 className="text-xl sm:text-2xl font-bold leading-tight mb-3">{slide.heading}</h3>
                        <div className="grid gap-3 lg:grid-cols-3 items-start">
                            <a href={img} target="_blank" rel="noopener noreferrer" className="block lg:col-span-2 rounded bg-white overflow-hidden" title="Open the picture full size">
                                <img src={img} alt={`Screenshot: ${slide.heading || ''}`} className="block w-full h-auto object-contain mx-auto" style={{ maxHeight: '62vh' }} />
                            </a>
                            <ul className="space-y-1.5 text-sm sm:text-base">
                                {(slide.bullets || []).map((b, i) => <li key={i} className="flex gap-2"><span className="text-indigo-300">▸</span><span>{b}</span></li>)}
                            </ul>
                        </div>
                    </div>
                ) : (
                <div className="p-5 sm:p-8 pb-24 sm:pb-24" aria-live="polite">
                    <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs uppercase tracking-wider text-indigo-200 mb-1">Slide {idx + 1} of {n}</p>
                            <h3 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">{slide.heading}</h3>
                            <ul className="space-y-2 text-base sm:text-lg">
                                {(slide.bullets || []).map((b, i) => <li key={i} className="flex gap-2"><span className="text-indigo-300">▸</span><span>{b}</span></li>)}
                            </ul>
                        </div>
                        {slide.visual && (
                            <div className={`hidden sm:flex shrink-0 w-40 items-center justify-center text-center ${[...slide.visual].length <= 4 ? 'text-6xl' : 'text-sm text-indigo-100 italic'}`} aria-hidden="true">
                                {slide.visual}
                            </div>
                        )}
                    </div>
                </div>
                )}
                {captions && caption && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-black bg-opacity-70 text-sm sm:text-base text-center" role="status">{caption}</div>
                )}
            </div>

            <div className="h-2 bg-slate-200 rounded cursor-pointer" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
                aria-label="Progress" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); go(Math.floor(((e.clientX - r.left) / r.width) * n)); }}>
                <div className="h-2 bg-slate-800 rounded" style={{ width: `${pct}%` }} />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
                <Btn kind="ghost" onClick={() => act('prev')} disabled={idx === 0} aria-label="Previous slide">◀ Prev</Btn>
                <Btn onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>{playing ? '❚❚ Pause' : '▶ Play'}</Btn>
                <Btn kind="ghost" onClick={() => act('next')} disabled={idx >= n - 1} aria-label="Next slide">Next ▶</Btn>
                <label className="flex items-center gap-1" title={voiceAvailable ? 'Play the narration aloud' : 'This browser cannot speak; the slides advance on a timer with captions.'}>
                    <input type="checkbox" checked={voiceOn && voiceAvailable} disabled={!voiceAvailable} onChange={(e) => setVoiceOn(e.target.checked)} /> Voice
                </label>
                <label className="flex items-center gap-1">Speed
                    <select className="border border-slate-300 rounded px-1 py-0.5" value={rate} onChange={(e) => setRate(Number(e.target.value))}>
                        {RATES.map((r) => <option key={r} value={r}>{r}×</option>)}
                    </select>
                </label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={captions} onChange={(e) => setCaptions(e.target.checked)} /> Captions</label>
                <Btn kind="ghost" onClick={() => setTranscript((t) => !t)}>{transcript ? 'Hide transcript' : 'Show transcript'}</Btn>
                <span className="text-xs text-slate-500 ml-auto">{pct}% · {seen.length}/{n} viewed</span>
            </div>
            {!voiceAvailable && <p className="text-xs text-slate-500">Spoken narration is not available in this browser: Play advances the slides on a timer and the narration shows as captions.</p>}
            <p className="text-xs text-slate-500 hidden sm:block">Keys: ← → slides · Space play/pause · Home/End · C captions · T transcript. On a phone, swipe.</p>

            {transcript && (
                <div className="border rounded p-3 max-h-80 overflow-y-auto text-sm space-y-3 bg-slate-50">
                    {slides.map((s, i) => (
                        <div key={i}>
                            <button type="button" className={`font-semibold text-left ${i === idx ? 'text-indigo-700' : 'text-slate-800'} underline-offset-2 hover:underline`} onClick={() => go(i)}>{i + 1}. {s.heading}</button>
                            <p className="text-slate-700">{s.narration}</p>
                        </div>
                    ))}
                </div>
            )}
            {notesMd && <details className="text-sm"><summary className="cursor-pointer text-slate-600">Sources and notes</summary><div className="mt-2"><Markdown text={notesMd} /></div></details>}
        </div>
    );
};

export default SlidePlayer;
